import { useId } from "react";
import type { JSX } from "react";
import { Link } from "react-router";
import { routePaths } from "../../app/navigation";
import { useI18n } from "../../shared/i18n/I18nContext";
import { cx } from "../../shared/ui/classNames";
import { useFocusOnMount } from "../../shared/ui/focus";
import styles from "../../shared/ui/ui.module.css";
import type { ActiveLocationCapture } from "./useLocationCapture";

/** Properties of {@link LocationNotice}. */
export interface LocationNoticeProps {
  /** The statement of the employer. */
  readonly policy: ActiveLocationCapture;
  /** Remembers the information and clocks with the location. */
  readonly onConfirm: () => void;
  /** Clocks without the location; the information appears again next time. */
  readonly onWithoutLocation: () => void;
  /** Closes the information with Escape without clocking. */
  readonly onDismiss: () => void;
}

/**
 * Information under Art. 13 GDPR before the location is captured for the first time, and again after
 * the employer changed its statement (T-175, ADR-0144): purpose, legal basis, the right to object,
 * rounding, retention, the moment of the capture and that clocking works without it. The second
 * button clocks without a location, so leaving the information never loses a clock event; Escape only
 * closes it. It appears below the clock action like the confirmation of an early start and receives
 * the focus.
 *
 * @param props - Component properties.
 * @returns The information.
 */
export function LocationNotice(props: LocationNoticeProps): JSX.Element {
  const { t } = useI18n();
  const panelRef = useFocusOnMount<HTMLDivElement>();
  const titleId = useId();
  const { policy } = props;
  const basis =
    policy.legalBasis === "WorksAgreement"
      ? t("location.basis.WorksAgreement", { reference: policy.legalBasisReference ?? "" })
      : t("location.basis.LegitimateInterest");

  const notice = (
    <div
      ref={panelRef}
      role="group"
      aria-labelledby={titleId}
      tabIndex={-1}
      className={cx(styles["alert"], styles["alertInfo"], styles["confirmAction"])}
      onKeyDown={(event) => {
        if (event.key === "Escape") {
          event.preventDefault();
          // Escape closes only the information, not a panel around it (F-75).
          event.stopPropagation();
          props.onDismiss();
        }
      }}
    >
      <p id={titleId}>
        <strong>{t("location.notice.title")}</strong>
      </p>
      <p>{t("location.notice.purpose", { purpose: policy.purpose ?? "" })}</p>
      <p>{t("location.notice.basis", { basis })}</p>
      <p>{t("location.notice.how", { days: policy.retentionDays ?? 0 })}</p>
      <p>{t("location.notice.choice")}</p>
      {/* LEGAL-REVIEW: information on the right to object (Art. 21 (1), (4) GDPR) with legitimate interest. */}
      {policy.legalBasis === "LegitimateInterest" ? <p>{t("location.notice.objection")}</p> : null}
      <p>
        <Link to={routePaths.privacy}>{t("location.notice.privacyLink")}</Link>
      </p>
      <div className={styles["buttonGrid"]}>
        <button type="button" className={styles["button"]} onClick={props.onWithoutLocation}>
          {t("location.notice.withoutLocation")}
        </button>
        <button
          type="button"
          className={cx(styles["button"], styles["primary"])}
          onClick={props.onConfirm}
        >
          {t("location.notice.confirm")}
        </button>
      </div>
    </div>
  );

  return notice;
}
